import { Box, Card } from '@material-ui/core';
import React, { CSSProperties, useEffect, useState } from 'react';
import PieChartSelection from './Chart';
import ChartTable from './chartTable';

const styles = {
  pichartSection: { height: '100%' },
  pichartBoxSection: { padding: '16px' },
  tittle: {
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: '1.5rem',
    fontWeight: '600',
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    paddingBottom: '2%',
  },
};

export type SubscriptionRevenueData = {
  new?: number;
  recurring?: number;
};

const LABELS = ['New', 'Recurring'];
const PIE_CHART_SIZE = 400;

const formatRevenue = (value: number) =>
  Number.isFinite(value)
    ? `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    : '$0.00';

const SubscriptionRevenueChart = (props: {
  subscriptionRevenueData?: SubscriptionRevenueData | null;
}) => {
  const { subscriptionRevenueData } = props;
  const [series, setSeries] = useState<number[]>([]);
  const [chartData, setChartData] = useState<
    { type: string; revenue: string }[]
  >([]);
  const [options, setOptions] = useState<any>({
    chart: {
      type: 'pie',
      id: 'subscription-revenue',
    },
    tooltip: {
      y: {
        formatter: (val: number) => formatRevenue(val),
      },
    },
    responsive: [
      {
        breakpoint: undefined,
        options: {
          legend: { position: 'bottom' },
        },
      },
    ],
  });

  const title = 'Subscription Revenue';
  const columns = [
    { field: 'type', headerName: 'Type' },
    { field: 'revenue', headerName: 'Revenue' },
  ];

  useEffect(() => {
    const data = subscriptionRevenueData ?? {};
    const newVal = Number(data.new ?? 0);
    const recurringVal = Number(data.recurring ?? 0);
    const rows = [
      { type: 'New', revenue: formatRevenue(newVal) },
      { type: 'Recurring', revenue: formatRevenue(recurringVal) },
    ];
    setChartData(rows);
    setSeries([newVal, recurringVal]);
    setOptions((prev: any) => ({
      ...prev,
      labels: LABELS,
      tooltip: {
        y: {
          formatter: (val: number) => formatRevenue(val),
        },
      },
    }));
  }, [subscriptionRevenueData]);

  const allZero = series.every((v) => v === 0);

  return (
    <Card style={styles.pichartSection as CSSProperties}>
      <Box style={styles.pichartBoxSection as CSSProperties}>
        <div style={styles.tittle as CSSProperties}>{title}</div>
        {!allZero && (
          <div style={{ width: PIE_CHART_SIZE, minHeight: 220 }}>
            <PieChartSelection
              series={series}
              options={options}
              title={title}
              next="subscription-revenue"
            />
          </div>
        )}
        {allZero && (
          <div style={{ padding: '16px 0', color: 'rgba(0,0,0,0.6)' }}>
            No subscription revenue data for the selected period.
          </div>
        )}
        <ChartTable rows={chartData} columns={columns} />
      </Box>
    </Card>
  );
};

export default SubscriptionRevenueChart;
